import { createContext, useContext, type ComponentType, type ReactNode } from 'react';

/** Who is looking at the page, as the host app knows it. */
export interface Viewer {
  isAuthenticated: boolean;
  /** True while auth or subscription state is still loading. */
  loading: boolean;
  /** True only while sign-in itself is being checked (subscription may still be loading after). */
  authLoading?: boolean;
  firstName?: string;
  /** The analysis model the viewer has selected (1 by default). */
  analysisTypeId: number;
  /**
   * The analysis models this viewer is allowed to switch between, when the app has
   * granted that feature. Fewer than two means there is nothing to choose and no
   * control is shown — which is the case for everyone but staff.
   */
  analysisTypes?: AnalysisModel[];
  /**
   * Switch the model. Lives here rather than on AppShell because it closes over the
   * host app's React state, and useViewer() is the hook that can reach it.
   */
  setAnalysisType?(id: number): void;
  /** Has a paid subscription (not the default/promo package). */
  isSubscribed: boolean;
  /** Can open Track Statistics (the track_stats user feature). Null or absent while it is still being checked. */
  trackStats?: boolean | null;
  /** Can open Our multis and Quaddies (the multis user feature). Null or absent while it is still being checked. */
  multis?: boolean | null;
  /** Can open Build your own and My multis (the multi_builder user feature). Null or absent while it is still being checked. */
  multiBuilder?: boolean | null;
  /** Can use the chat with the team (the customer_chat user feature). Null or absent while it is still being checked. */
  chat?: boolean | null;
}

/**
 * The seam between the shared customer UI and each app's own auth,
 * subscription and analytics code (ADR-0008). Temporary: it goes away when the
 * app shells are merged and those services move into shared.
 */
/** One selectable analysis model, as the host app names it. */
export interface AnalysisModel {
  id: number;
  /** Short label for the switch, e.g. "2". */
  name: string;
  /** Full name, for the tooltip and screen readers, e.g. "Slot 2 (Alt)". */
  title?: string;
}

export interface AppShell {
  useViewer(): Viewer;
  /** Records an analytics event. */
  track(event: string, params?: Record<string, unknown>): void;
  /** Route guard: signed-in only. */
  Protected: ComponentType<{ children: ReactNode }>;
  /** Route guard: paid subscription only. */
  Subscribed: ComponentType<{ children: ReactNode }>;
  /** The app's own plans-and-promo paywall for a locked meeting; a plain subscribe card is shown without one. */
  Paywall?: ComponentType<PaywallProps>;
  /**
   * The affiliate and discount codes the app attaches to a checkout (its referral tracking
   * and the signed-in customer's assigned code). Without it a checkout carries only a
   * ?discount= from the link.
   */
  useCheckoutCodes?(): CheckoutCodes;
}

export interface CheckoutCodes {
  affiliateCode(): string | null;
  userDiscountCode(): Promise<string | null>;
  /** The running promotion's code, if any. */
  promotionCode?: string | null;
}

export interface PaywallProps {
  trackName: string;
  state?: string;
  track?: string;
  date?: string;
}

const ShellContext = createContext<AppShell | null>(null);

export function ShellProvider({ shell, children }: { shell: AppShell; children: ReactNode }) {
  return <ShellContext.Provider value={shell}>{children}</ShellContext.Provider>;
}

/** The shell if there is one: for code that can run outside it (the tour provider in tests). */
export function useOptionalShell(): AppShell | null {
  return useContext(ShellContext);
}

export function useShell(): AppShell {
  const shell = useContext(ShellContext);
  if (!shell) throw new Error('useShell must be used inside <ShellProvider>');
  return shell;
}

export function useViewer(): Viewer {
  return useShell().useViewer();
}
